/**
 * Client-side thumbnail generation (runs in the browser before upload).
 * - images: canvas resize (long edge 512px) → WebP
 * - videos: <video> seek to ~1s → canvas → JPEG
 * Returns null when generation fails (unsupported codec, black frames on
 * iOS HEVC, etc.) — callers fall back to type icons.
 */

const THUMB_LONG_EDGE = 512;
const IMAGE_QUALITY = 0.75;

function scaledSize(w: number, h: number): { width: number; height: number } {
  const scale = Math.min(1, THUMB_LONG_EDGE / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

export async function imageThumbnail(file: File): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = scaledSize(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    return await canvasToBlob(canvas, 'image/webp', IMAGE_QUALITY);
  } catch {
    return null;
  }
}

export async function videoThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    const cleanup = () => URL.revokeObjectURL(video.src);
    const finish = (b: Blob | null) => { cleanup(); resolve(b); };

    video.onerror = () => finish(null);
    video.onloadedmetadata = () => {
      if (!video.videoWidth || !video.videoHeight) return finish(null);
      const t = Math.min(1.0, video.duration * 0.1);
      video.onseeked = () => {
        try {
          const { width, height } = scaledSize(video.videoWidth, video.videoHeight);
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return finish(null);
          ctx.drawImage(video, 0, 0, width, height);
          canvasToBlob(canvas, 'image/jpeg', IMAGE_QUALITY).then(finish);
        } catch {
          finish(null);
        }
      };
      try { video.currentTime = t; } catch { finish(null); }
    };
    video.src = URL.createObjectURL(file);
  });
}

export async function makeThumbnail(file: File): Promise<Blob | null> {
  if (file.type.startsWith('image/')) return imageThumbnail(file);
  if (file.type.startsWith('video/')) return videoThumbnail(file);
  return null;
}

/** Thumb object key for a media key (mirrors backend thumbKeysFor). */
export function thumbKeyFor(key: string, isVideo: boolean): string {
  return `.thumbs/${key}.${isVideo ? 'jpg' : 'webp'}`;
}
